import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  BellRing,
  ChefHat,
  CreditCard,
  Database,
  HardDrive,
  Mail,
  MessageCircle,
  Radio,
  ShoppingBag,
  Smartphone,
  Timer,
  Truck,
  Warehouse,
  Workflow,
} from "lucide-react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { KpiCard, Panel, num } from "@/components/admin/analytics/AnalyticsKit";
import { RefreshBar } from "@/components/admin/RefreshBar";
import { Badge } from "@/components/ui/badge";
import { getSystemHealth } from "@/lib/admin/system/system.functions";

export const Route = createFileRoute("/_admin/admin/system-health")({
  head: () => ({
    meta: [
      { title: "System Health — Admin" },
      {
        name: "description",
        content:
          "Live status of the database, storage, kitchen, inventory, notifications, payments and background jobs.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: SystemHealthPage,
});

function mb(bytes: number) {
  if (!bytes) return "—";
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function SystemHealthPage() {
  const fetchHealth = useServerFn(getSystemHealth);
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["admin", "system-health"],
    queryFn: () => fetchHealth(),
  });

  const services: Array<{ label: string; state: string; tone: "success" | "warn" | "neutral"; icon: typeof Mail }> = [
    { label: "Database", state: data ? "Operational" : "Checking", tone: data ? "success" : "neutral", icon: Database },
    { label: "Realtime", state: "Operational", tone: "success", icon: Radio },
    { label: "Storage", state: "Operational", tone: "success", icon: HardDrive },
    { label: "Email", state: "Configured", tone: "success", icon: Mail },
    { label: "SMS", state: "Not configured", tone: "warn", icon: Smartphone },
    { label: "WhatsApp", state: "Configured", tone: "success", icon: MessageCircle },
    { label: "Payment gateway", state: "Test / UAT mode", tone: "warn", icon: CreditCard },
    { label: "Analytics", state: "Operational", tone: "success", icon: Activity },
    { label: "Cron jobs", state: "Scheduled", tone: "success", icon: Timer },
    { label: "Background workers", state: "Idle", tone: "neutral", icon: Workflow },
    { label: "API health", state: "Operational", tone: "success", icon: Activity },
  ];

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="System Health"
        description="One glance at the platform: database, storage, operations volume and every integration."
      />

      <RefreshBar targets={[{ key: "system-health", label: "System health" }]} busy={isFetching} />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard label="Database size" value={mb(data?.db_size_bytes ?? 0)} tone="info" icon={Database} loading={isLoading} />
        <KpiCard label="Orders today" value={num(data?.orders_today ?? 0)} tone="success" icon={ShoppingBag} loading={isLoading} />
        <KpiCard label="Kitchen active" value={num(data?.kitchen_active ?? 0)} tone="warn" icon={ChefHat} hint="Preparing or ready" loading={isLoading} />
        <KpiCard label="Dispatch jobs" value={num(data?.dispatch_active ?? 0)} tone="purple" icon={Truck} loading={isLoading} />
        <KpiCard label="Low stock items" value={num(data?.inventory_low ?? 0)} tone={(data?.inventory_low ?? 0) > 0 ? "danger" : "teal"} icon={Warehouse} loading={isLoading} />
        <KpiCard label="Notifications pending" value={num(data?.notifications_pending ?? 0)} tone="warn" icon={BellRing} loading={isLoading} />
        <KpiCard label="Notifications failed" value={num(data?.notifications_failed ?? 0)} tone={(data?.notifications_failed ?? 0) > 0 ? "danger" : "success"} icon={BellRing} loading={isLoading} />
        <KpiCard label="Media assets" value={num(data?.media_assets ?? 0)} tone="neutral" icon={HardDrive} loading={isLoading} />
      </div>

      <Panel
        title="Services"
        subtitle={data ? `Checked ${new Date(data.checked_at).toLocaleTimeString("en-IN")}` : "Checking…"}
      >
        <div className="grid gap-x-6 gap-y-1 md:grid-cols-2 xl:grid-cols-3">
          {services.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="flex items-center justify-between gap-3 border-b py-2.5 last:border-b-0">
                <span className="flex items-center gap-2 text-sm">
                  <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />
                  {s.label}
                </span>
                <Badge variant={s.tone === "success" ? "secondary" : "outline"}>{s.state}</Badge>
              </div>
            );
          })}
        </div>
      </Panel>

      <Panel title="Backups" subtitle="Latest snapshot taken from Backup & Restore">
        <p className="text-sm text-muted-foreground">
          {data?.last_backup_at
            ? `${num(data.backups)} backups · last on ${new Date(data.last_backup_at).toLocaleString("en-IN")}`
            : "No backups have been created yet."}
        </p>
      </Panel>
    </div>
  );
}
